<template>
    <div>
        <title-bar :title-stack="titleStack" />
        <hero-bar>
            Formulare manuale din {{ formsPageYear }} ({{ formsCount }})
            <div slot="right">
                <div v-for="(column, index) in columnsVisible" :key="index" class="control is-inline-block">
                    <b-checkbox v-model="column.display">
                        {{ column.title }}
                    </b-checkbox>
                </div>
            </div>
        </hero-bar>
        <section class="section is-main-section custom-section">
            <b-modal
                full-screen
                v-model="isReviewModalActive"
                has-modal-card
                trap-focus
                :destroy-on-hide="true"
                aria-role="dialog"
                aria-modal
            >
                <template>
                    <review-form
                        @close="closeModal"
                        @reviewed="setNextFormForReview"
                        @next="setNextFormForReview"
                    ></review-form>
                </template>
            </b-modal>
            <b-modal
                full-screen
                v-model="isFormEditorActive"
                has-modal-card
                trap-focus
                :destroy-on-hide="true"
                aria-role="dialog"
                aria-modal
            >
                <template>
                    <form-editor @close="closeEditModal"></form-editor>
                </template>
            </b-modal>
            <card-component title="Lista formulare" class="has-table has-mobile-sort-spaced" icon="account-multiple">
                <div class="columns column-control is-vcentered">
                    <div class="column is-4">
                        <b-field class="is-fullwidth" label="Cautare">
                            <b-input
                                :loading="isLoading"
                                @keypress.native.enter="onSearch"
                                placeholder="Cauta dupa Nume/Prenume/CNP/Cont/Telefon"
                                type="search"
                                v-model.trim="search"
                                icon="magnify"
                                class="is-fullwidth"
                            >
                            </b-input>
                            <p class="control">
                                <b-button @click="onSearch" type="is-primary" label="Cauta" />
                            </p>
                        </b-field>
                    </div>
                    <div class="column is-2">
                        <b-field label="Filtru status">
                            <b-select @input="onStatusChange" v-model="status" placeholder="Select a name" expanded>
                                <option value="">Toate</option>
                                <option value="0">Pending</option>
                                <option value="1">Aprobate</option>
                            </b-select>
                        </b-field>
                    </div>
                </div>

                <b-table
                    :loading="isLoading"
                    :checkable="false"
                    :paginated="true"
                    :total="formsCount"
                    :per-page="formsLimit"
                    :current-page="this.currentPage"
                    :striped="true"
                    :hoverable="true"
                    :data="formsList"
                    detailed
                    detail-key="id"
                    show-detail-icon
                    backend-sorting
                    backend-pagination
                    @page-change="onPageChange"
                    @sort="onSort"
                    aria-next-label="Next page"
                    aria-previous-label="Previous page"
                    aria-page-label="Page"
                    aria-current-label="Current page"
                    :default-sort-direction="defaultSortOrder"
                    :default-sort="[defaultSortField, defaultSortOrder]"
                    :debounce-search="1000"
                >
                    <b-table-column label="ID" field="id" sortable v-slot="props">
                        {{ props.row.id }}
                    </b-table-column>
                    <b-table-column :custom-search="onCustomSearch" label="Nume" field="nume" sortable v-slot="props">
                        {{ props.row.nume }}
                    </b-table-column>
                    <b-table-column label="Prenume" field="prenume" sortable v-slot="props">
                        {{ props.row.prenume }}
                    </b-table-column>
                    <b-table-column label="Judet" field="judet" sortable v-slot="props">
                        {{ props.row.forms_address.judet }}
                    </b-table-column>
                    <b-table-column label="Localitate" field="localitate" sortable v-slot="props">
                        {{ props.row.forms_address.localitate }}
                    </b-table-column>
                    <b-table-column label="CNP" field="cnp" sortable v-slot="props">
                        {{ props.row.cnp }}
                    </b-table-column>
                    <b-table-column label="Cont" field="account.iban" sortable v-slot="props">
                        {{ props.row.account.iban.slice(props.row.account.iban.length - 4) }}
                    </b-table-column>
                    <b-table-column
                        :visible="columnsVisible['telefon'].display"
                        :label="columnsVisible['telefon'].title"
                        centered
                        field="telefon"
                        sortable
                        v-slot="props"
                    >
                        {{ props.row.telefon }}
                    </b-table-column>
                    <b-table-column
                        centered
                        :visible="columnsVisible['email'].display"
                        :label="columnsVisible['email'].title"
                        field="Email"
                        sortable
                        v-slot="props"
                    >
                        {{ props.row.email }}
                    </b-table-column>
                    <b-table-column centered label="Perioada" field="perioada" sortable v-slot="props">
                        {{ props.row.perioada > 1 ? `${props.row.perioada} ani` : '1 an' }}
                    </b-table-column>
                    <b-table-column centered label="An" field="year" sortable v-slot="props">
                        {{ props.row.year }}
                    </b-table-column>
                    <b-table-column centered label="Status" field="status" sortable v-slot="props">
                        <b-tag
                            :type="
                                +props.row.status == 0
                                    ? 'is-info'
                                    : +props.row.status == -1
                                    ? 'is-danger'
                                    : 'is-success'
                            "
                            >{{
                                +props.row.status == 0 ? 'Pending' : +props.row.status == -1 ? 'Refuzat' : 'Aprobat'
                            }}</b-tag
                        >
                    </b-table-column>
                    <b-table-column
                        centered
                        sortable
                        :visible="columnsVisible['createdAt'].display"
                        :label="columnsVisible['createdAt'].title"
                        field="createdAt"
                        v-slot="props"
                    >
                        <small class="has-text-grey is-abbr-like">{{
                            moment(props.row.createdAt).format('DD/MM/YYYY h:mm:ss')
                        }}</small>
                    </b-table-column>
                    <b-table-column custom-key="actions" cell-class="is-actions-cell" v-slot="props">
                        <div class="buttons is-right">
                            <b-tooltip label="Modifica formularul" type="is-dark" position="is-top">
                                <b-button
                                    @click="openFormEditor(props.row.id)"
                                    :disabled="props.row.telefon == null || props.row.telefon === ''"
                                    :loading="isApprovingLoading"
                                    type="is-black"
                                    icon-right="file-edit-outline"
                                    size="is-small"
                                >
                                </b-button>
                            </b-tooltip>
                            <b-tooltip
                                v-if="props.row.status == 0"
                                label="Verifica formularul"
                                type="is-dark"
                                position="is-top"
                            >
                                <b-button
                                    @click="reviewForm(props.row.id)"
                                    :loading="isApprovingLoading"
                                    type="is-primary"
                                    icon-right="check-underline"
                                    size="is-small"
                                >
                                </b-button>
                            </b-tooltip>
                            <b-tooltip label="Decarca PDF" type="is-dark" position="is-top">
                                <b-button
                                    @click="downloadFormPDF(props.row.id)"
                                    :disabled="props.row.telefon == null || props.row.telefon === ''"
                                    :loading="isDownloadLoading"
                                    type="is-info"
                                    icon-right="file-pdf-box-outline"
                                    size="is-small"
                                >
                                </b-button>
                            </b-tooltip>
                            <b-tooltip label="Sterge formular" type="is-dark" position="is-left">
                                <b-button
                                    @click="deleteForm(props.row.id)"
                                    :disabled="props.row.telefon == null || props.row.telefon === ''"
                                    :loading="isLoading"
                                    type="is-danger"
                                    icon-right="trash-can"
                                    size="is-small"
                                >
                                </b-button>
                            </b-tooltip>
                        </div>
                    </b-table-column>
                    <template #detail="props">
                        <article>
                            <table class="table is-bordered full-width">
                                <thead>
                                    <tr>
                                        <th>Initiala</th>
                                        <th>Strada</th>
                                        <th>Numar</th>
                                        <th>Bloc</th>
                                        <th>Scara</th>
                                        <th>Etaj</th>
                                        <th>Ap.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{{ props.row.initiala || '-' }}</td>
                                        <td>{{ props.row.forms_address.strada || '-' }}</td>
                                        <td>{{ props.row.forms_address.numar || '-' }}</td>
                                        <td>{{ props.row.forms_address.bloc || '-' }}</td>
                                        <td>{{ props.row.forms_address.scara || '-' }}</td>
                                        <td>{{ props.row.forms_address.etaj || '-' }}</td>
                                        <td>{{ props.row.forms_address.apartament || '-' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </article>
                    </template>
                    <section slot="empty" class="section">
                        <div class="content has-text-grey has-text-centered">
                            <template v-if="isLoading">
                                <p>
                                    <b-icon icon="dots-horizontal" size="is-large" />
                                </p>
                                <p>Se incarca...</p>
                            </template>
                            <template v-else>
                                <p>
                                    <b-icon icon="emoticon-sad" size="is-large" />
                                </p>
                                <p>Nu exista date&hellip;</p>
                            </template>
                        </div>
                    </section>
                </b-table>
            </card-component>
        </section>
    </div>
</template>

<script>
import CardComponent from '@/components/CardComponent';
import TitleBar from '@/components/TitleBar';
import HeroBar from '@/components/HeroBar';
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import * as toast from '@/utils/toast';
import * as dialogs from '@/utils/dialog';
import debounce from 'lodash/debounce';
import { createBlob } from '@/utils/functions';
import ReviewForm from './components/ManualFormReview';
const FormEditor = () => import('./components/FormEditor');

export default {
    name: 'Formulare',
    components: {
        CardComponent,
        HeroBar,
        TitleBar,
        ReviewForm,
        FormEditor,
    },
    data: () => ({
        isLoading: false,
        moment,
        columnsVisible: {
            telefon: { title: 'Telefon', display: false },
            email: { title: 'Email', display: false },
            createdAt: { title: 'Data', display: false },
        },
        isApprovingLoading: false,
        isDownloadLoading: false,
        isReviewModalActive: false,
        isFormEditorActive: false,
        isFormEditorActive: false,
    }),
    computed: {
        ...mapState({
            defaultSortField: (state) => state.admin.formular230.forms.sortField,
            defaultSortOrder: (state) => state.admin.formular230.forms.sortOrder,
        }),
        ...mapGetters({
            currentPage: 'admin/currentPage',
            formsList: 'admin/formsList',
            formsCount: 'admin/formsCount',
            formsLimit: 'admin/formsLimit',
            formsSearch: 'admin/formsSearch',
            formsPageYear: 'admin/formsPageYear',
            statusFilter: 'admin/statusFilter',
            currentManualFormId: 'admin/currentManualFormId',
        }),
        titleStack() {
            return ['Admin', 'Declaratie 230', 'Formulare'];
        },
        search: {
            get() {
                return this.formsSearch;
            },
            set(value) {
                this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'search', value });
            },
        },
        status: {
            get() {
                return this.statusFilter;
            },
            set(value) {
                this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'status', value });
            },
        },
    },
    methods: {
        async onStatusChange() {
            return this.getForms();
        },
        async getForms() {
            this.isLoading = true;
            try {
                await this.$store.dispatch('admin/GetForms230Manuale');
            } catch (err) {
                return false;
            } finally {
                this.isLoading = false;
            }
        },
        async deleteForm(id) {
            const confirmation = await dialogs.confirm('Confirmare', `Sterg formularul ${id}`, 'Confirm', 'is-warning');
            if (!confirmation.result) return false;
            this.isLoading = true;
            return this.$store
                .dispatch('admin/DeleteForm230', id)
                .then((res) => {
                    return toast.success('Formularul a fost sters', 5000, 'is-top');
                })
                .catch((err) => toast.error('Eroare la stergerea formularului'))
                .finally(() => (this.isLoading = false));
        },
        async onPageChange(page) {
            this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'page', value: page });
            return this.getForms();
        },
        async onSort(field, order) {
            this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'sortField', value: field });
            this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'sortOrder', value: order });
            return this.getForms();
        },
        onSearch() {},
        async downloadFormPDF(formId) {
            this.isDownloadLoading = true;
            try {
                const pdfData = await this.$store.dispatch('admin/DownloadFormPDF', formId);
                const blobUrl = createBlob(pdfData.mime, pdfData.data);
                const dlnk = document.createElement('a');
                dlnk.href = blobUrl;
                dlnk.setAttribute('target', '_blank');
                dlnk.download = 'formular-' + formId + '.' + pdfData.extension;
                document.body.appendChild(dlnk);
                dlnk.click();
                dlnk.remove();
            } catch (e) {
                console.log(e);
                toast.error('Eroare la descarcarea formularului');
            } finally {
                this.isDownloadLoading = false;
            }
        },
        closeModal() {
            this.isReviewModalActive = false;
        },
        closeEditModal() {
            this.isFormEditorActive = false;
        },
        openFormEditor(formId) {
            this.$store.commit('admin/SET_CURRENT_FORM_ID', formId);
            return (this.isFormEditorActive = true);
        },
        async setNextFormForReview(recursive = false, index = -1) {
            if (this.formsList == null || this.formsList.length == 0) {
                toast.error('Nu mai exista formulare de verificat');
                return (this.isReviewModalActive = false);
            }
            if (index != -1) {
                if (typeof this.formsList[index] === 'undefined') {
                    const pages = Math.ceil(this.formsCount / this.formsLimit);
                    if (this.currentPage >= pages) {
                        if (this.currentPage == 1) {
                            toast.error('Nu mai exista formulare de verificat');
                            return (this.isReviewModalActive = false);
                        }
                        this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', {
                            field: 'page',
                            value: 1,
                        });
                        await this.getForms();
                        if (!recursive) {
                            return this.setNextFormForReview(true, 0);
                        } else {
                            toast.error('Nu mai exista formulare');
                            return (this.isReviewModalActive = false);
                        }
                    } else {
                        this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', {
                            field: 'page',
                            value: this.currentPage + 1,
                        });
                        await this.getForms();
                        if (!recursive) {
                            return this.setNextFormForReview(true, 0);
                        } else {
                            toast.error('Eroare la preluarea urmatorului formular');
                            return (this.isReviewModalActive = false);
                        }
                    }
                } else {
                    let found = false;
                    while (!found) {
                        if (typeof this.formsList[index] === 'undefined') {
                            return this.setNextFormForReview(false, index);
                        }
                        if (this.formsList[index].status != 0) {
                            index++;
                        } else {
                            found = true;
                        }
                    }
                    return this.$store.commit('admin/SET_CURRENT_MANUAL_FORM_ID', this.formsList[index].id);
                }
            } else {
                let currentFormIndex = this.formsList.findIndex((form) => form.id == this.currentManualFormId);
                if (currentFormIndex < 0) {
                    return this.setNextFormForReview(false, 0);
                } else {
                    return this.setNextFormForReview(false, ++currentFormIndex);
                }
            }
        },
        async reviewForm(formid) {
            this.$store.commit('admin/SET_CURRENT_MANUAL_FORM_ID', formid);
            return (this.isReviewModalActive = true);
        },
        async onCustomSearch(a, input) {
            console.log(a, input);
        },
        openFormEditor(formId) {
            this.$store.commit('admin/SET_CURRENT_FORM_ID', formId);
            return (this.isFormEditorActive = true);
        },
    },
    watch: {
        '$route.params.year'(val) {
            this.$store.commit('admin/SET_FORMULARE_MANUALE_PAGE_YEAR', +val);
            this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'manual', value: 1 });
            return this.getForms();
        },
    },
    async created() {
        if (this.$route.params?.year == null) {
            return this.$router.push({ name: 'FormulareManuale', params: { year: 2025 } });
        }
        console.log(+this.$route.params.year);
        this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'manual', value: 1 });
        this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'status', value: 0 });
        this.$store.commit('admin/SET_FORMULARE_MANUALE_PAGE_YEAR', +this.$route.params.year);
        this.onSearch = debounce(() => {
            return this.getForms();
        }, 300);
        await this.getForms();
    },
    beforeDestroy() {
        this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'status', value: '' });
        this.$store.commit('admin/SET_FORMS230_PAGINATION_FIELD', { field: 'manual', value: 0 });
    },
};
</script>

<style lang="scss" scoped>
::v-deep .level {
    align-items: baseline;
}

.is-fullwidth {
    width: 100%;
}
</style>
